import { Option } from '@mm/components';
import {
  FBCommentGuardSlideoutBanner,
  FBKeywordSlideoutBanner,
  FBShareLinksSlideoutBanner,
  IGCommentGuardSlideoutBanner,
  IGKeywordSlideoutBanner,
  IGLiveSlideoutBanner,
  IGShareLinksSlideoutBanner,
  IGStoryMentionSlideoutBanner,
  IGStoryReactionSlideoutBanner,
  IGWelcomerSlideoutBanner,
  WebsiteChatSlideoutBanner,
} from '@mm/icons/SlideoutBanners';
import { SubscriptionFeatureFlag } from '@mm/state/controllers/FeatureFlags';
import { CompanyLevelFeatureFlag } from '@mm/state/model/CompanyModel';
import { PromoterTypes } from '@mm/state/model/PromoterModel';
import React from 'react';
import { ReactComponent as FacebookKey } from './icons/fb-key.svg';
import { ReactComponent as FacebookLink } from './icons/fb-link.svg';
import { ReactComponent as FacebookMessage } from './icons/fb-message.svg';
import { ReactComponent as FacebookSpeaker } from './icons/fb-speaker.svg';
import { ReactComponent as FacebookWave } from './icons/fb-wave.svg';
import { ReactComponent as InstagramKey } from './icons/ig-key.svg';
import { ReactComponent as InstagramLink } from './icons/ig-link.svg';
import { ReactComponent as InstagramLive } from './icons/ig-live.svg';
import { ReactComponent as InstagramMention } from './icons/ig-mention.svg';
import { ReactComponent as InstagramMessage } from './icons/ig-message.svg';
import { ReactComponent as InstagramReaction } from './icons/ig-reaction.svg';
import { ReactComponent as InstagramWave } from './icons/ig-wave.svg';
import { ReactComponent as WebChat } from './icons/web-chat.svg';
import { ReactComponent as XRayEmailIcon } from './icons/x-ray.svg';
import { ReactComponent as DocumentFile } from 'assets/csv-automation.svg';
import { ReactComponent as Audience } from './icons/audience.svg';
import { TriggerCardDefinition } from './types';
import { useDisabledInputTriggers } from './use-disabled-input-triggers';
import { useInputTriggersIdentification } from './use-input-triggers-identification';

export type TriggerType =
  | 'facebook_keywords'
  | 'fb_comment_guard'
  | 'facebook_catch_all'
  | 'instagram_catch_all'
  | 'import_contacts'
  | 'instagram_comment_guard'
  | 'instagram_keywords'
  | 'instagram_live_comment_guard'
  | 'instagram_story_mention'
  | 'instagram_story_reaction'
  | 'instagram_welcomer'
  | 'customer_chat'
  | 'welcome_page'
  | 'link'
  | 'instagram_link'
  | 'web_visitor_email_detector'
  | 'ctm_ads'
  | 'audience';

export const VISIBLE_TRIGGER_TYPES = [
  'web_visitor_email_detector',
  'import_contacts',
  'facebook_keywords',
  'fb_comment_guard',
  'instagram_comment_guard',
  'instagram_keywords',
  'instagram_live_comment_guard',
  'instagram_story_mention',
  'instagram_story_reaction',
  'instagram_welcomer',
  'customer_chat',
  'welcome_page',
  'link',
  'instagram_link',
  'ctm_ads',
  'audience',
] as const;
export type VisibleTriggerType = typeof VISIBLE_TRIGGER_TYPES[number];
export const CATEGORIES = [
  'featured',
  'facebook',
  'instagram',
  'webchat',
] as const;
export type Category = typeof CATEGORIES[number];

export const CATEGORY_NAMES: Record<Category, string> = {
  featured: 'Most Popular',
  facebook: 'Facebook',
  instagram: 'Instagram',
  webchat: 'Webchat',
};
export const TRIGGERS_BY_CATEGORY: Record<
  Category,
  null | VisibleTriggerType[]
> = {
  featured: ['web_visitor_email_detector', 'audience', 'import_contacts'],
  instagram: [
    'instagram_keywords',
    'instagram_welcomer',
    'instagram_comment_guard',
    'instagram_story_mention',
    'instagram_story_reaction',
    'instagram_live_comment_guard',
    'instagram_link',
  ],
  facebook: [
    'facebook_keywords',
    'welcome_page',
    'fb_comment_guard',
    'link',
    'ctm_ads',
  ],
  webchat: ['customer_chat'],
};

export interface Trigger {
  name: string;
  description?: string;
  color?: string;
  icon: React.ComponentType<any>;
  popular?: boolean;
  featured?: boolean;
  template:
    | {
        title: string;
        body: string;
      }
    | false;
  widgetsPreset?: WidgetsPreset;
  requiredFeature?: SubscriptionFeatureFlag | CompanyLevelFeatureFlag;
}

export type TemplateParams =
  | {
      // used to assign existing page
      inputTriggers: {
        kind: TriggerType;
        pageId?: number;
      }[];
    }
  | {
      collectionOptions: CollectionOption[];
      qualifyingQuestions: QualifyingQuestionOption;
      inputTriggers: {
        kind: TriggerType;
        pageId?: number;
      }[];
      widgetsPreset?: WidgetsPreset;
      ctmAdId?: number;
    };

type CollectionOption = 'email' | 'phone';
export const COLLECTION_OPTIONS: Option<CollectionOption>[] = [
  { id: 'email', label: 'Email' },
  { id: 'phone', label: 'Phone Number' },
];

type QualifyingQuestionOption = 0 | 1 | 2 | 3 | 4;
export const QUALIFYING_QUESTION_OPTIONS: Option<QualifyingQuestionOption>[] = [
  { id: 0, label: '0' },
  { id: 1, label: '1' },
  { id: 2, label: '2' },
  { id: 3, label: '3' },
];

type WidgetsPreset = 'catch_all';

const colors = {
  lime: '#FCFFE6',
  gold: '#FFF1B8',
  blue: '#E6F7FF',
  volcano: '#FFF2E8',
  purple: '#F9F0FF',
  magenta: '#FFF0F6',
  blueGradient:
    'radial-gradient(97.01% 97.01% at 3.96% 2.99%, #F7F0EF 15.24%, #CEE1FF 50%, #85B4EE 100%)',
  pinkGradient:
    'radial-gradient(97.01% 97.01% at 3.96% 2.99%, #FFFAE4 4.17%, #FEC6E3 52.6%, #BFA8FF 100%)',
  limeGradient:
    'radial-gradient(97.01% 97.01% at 3.96% 2.99%, #FEEFC7 5.58%, #D0F0D9 44.46%, #86C7C3 100%)',
};

export const mapPromoterTypeToTriggerType = (kind: string): TriggerType => {
  switch (kind) {
    case PromoterTypes.CUSTOMER_CHAT:
      return 'customer_chat';
    case PromoterTypes.GETEMAILS:
      return 'web_visitor_email_detector';
    default:
      return kind as TriggerType;
  }
};

export const TRIGGER_DEFINITIONS: Record<TriggerType, Trigger> = {
  facebook_catch_all: {
    name: 'Engagements Autoresponder',
    icon: FacebookMessage,
    template: {
      title: '',
      body: '',
    },
  },
  instagram_catch_all: {
    name: 'Engagements Autoresponder',
    icon: InstagramMessage,
    template: {
      title: '',
      body: '',
    },
  },
  instagram_comment_guard: {
    name: 'Comments Autoresponder',
    description: 'Reply to Post Comments with a DM',
    icon: InstagramMessage,
    color: colors.magenta,
    popular: true,
    template: {
      title: 'Configure IG Comments Autoresponder Input Trigger',
      body: 'When someone leaves a comment on one of my Instagram posts',
    },
  },
  instagram_story_mention: {
    name: 'Story Mentions Autoresponder',
    description:
      'Reply with a DM to followers who mention you in their stories',
    icon: InstagramMention,
    color: colors.magenta,

    template: {
      title: 'Configure IG Story Mentions Autoresponder Input Trigger',
      body: 'When someone mentions me in their Instagram Story',
    },
  },
  instagram_story_reaction: {
    name: 'Story Reactions Autoresponder',
    description: 'Reply with a DM to followers who react to your stories',
    icon: InstagramReaction,
    color: colors.magenta,

    template: {
      title: 'Configure IG Story Reactions Autoresponder Input Trigger',
      body: 'When someone reacts to my Instagram story',
    },
  },
  instagram_welcomer: {
    name: 'DM Autoresponder',
    description: 'Automatically respond to new DMs',
    icon: InstagramWave,
    color: colors.magenta,
    template: {
      title: 'Configure IG DM Autoresponder Input Trigger',
      body: 'When someone sends me a DM for the first time',
    },
  },
  instagram_keywords: {
    name: 'Keywords',
    description: 'Get leads by prompting people to text you a keyword',
    icon: InstagramKey,
    color: colors.magenta,
    popular: true,
    template: {
      title: 'Configure IG Keywords Autoresponder Input Trigger',
      body: 'When someone sends me a DM containing a keyword',
    },
  },
  instagram_live_comment_guard: {
    name: 'Live Autoresponder',
    description:
      'Reply with a DM to followers who comment on your Instagram Live Video',
    icon: InstagramLive,
    color: colors.magenta,
    template: {
      title: 'Configure IG Live Autoresponder Input Trigger',
      body: 'When someone comments on my IG Live',
    },
  },
  instagram_link: {
    name: 'Share Link',
    description: 'Generate a link that opens up a Instagram DM with your Page',
    icon: InstagramLink,
    color: colors.magenta,
    template: {
      title: 'Configure Instagram Share Link Input Trigger',
      body: 'When someone clicks a link you’ve shared in an email or a webpage',
    },
  },
  link: {
    name: 'Share Link',
    description:
      'Generate a link that opens up a Facebook messenger Chat with your Page',
    icon: FacebookLink,
    color: colors.blue,
    template: {
      title: 'Configure Facebook Share Link Input Trigger',
      body: 'When someone clicks a link you’ve shared in an email or a webpage',
    },
  },
  fb_comment_guard: {
    name: 'Comments Autoresponder',
    description: 'Reply to Post Comments with a DM',
    icon: FacebookMessage,
    color: colors.blue,
    popular: true,
    template: {
      title: 'Configure FB Comments Autoresponder Input Trigger',
      body: 'When someone leaves a comment on one of my Facebook posts',
    },
  },
  facebook_keywords: {
    name: 'Keywords',
    description: 'Get leads by prompting people to text you a keyword',
    icon: FacebookKey,
    color: colors.blue,
    template: {
      title: 'Configure FB Keywords Autoresponder Input Trigger',
      body: 'When someone sends me a FB Message containing a keyword',
    },
  },
  welcome_page: {
    name: 'DM Autoresponder',
    description: 'Automatically respond to new Facebook Messages',
    icon: FacebookWave,
    color: colors.blue,
    template: {
      title: 'Configure FB DM Autoresponder Input Trigger',
      body: 'When someone sends me a FB Message for the first time',
    },
  },
  customer_chat: {
    name: 'Website Chat Widget',
    description:
      'Add an engaging and interactive Chat experience to your website',
    icon: WebChat,
    color: colors.volcano,
    template: {
      title: 'Configure Website Chat Widget Input Trigger',
      body: 'When someone opens the Website Chat widget on my website',
    },
  },
  web_visitor_email_detector: {
    name: 'X-Ray Pixel',
    description: 'Capture 10x more contacts from your website visitors',
    icon: XRayEmailIcon,
    color: colors.purple,
    template: false,
    featured: true,
  },
  import_contacts: {
    name: 'Upload CSV',
    description: 'Upload a CSV file',
    icon: DocumentFile,
    featured: true,
    color: colors.lime,
    template: false,
  },
  ctm_ads: {
    name: 'Click-to-Messenger Ads',
    description:
      'Create a Messenger ad campaign and automatically reply to users',
    icon: FacebookSpeaker,
    color: colors.blue,
    template: false,
  },
  audience: {
    name: 'Audience',
    description: 'Choose an audience from your existing leads',
    icon: Audience,
    color: colors.lime,
    template: false,
    featured: true,
  },
};

// tslint:disable-next-line:cyclomatic-complexity
export const disabledInputTriggerProps = (params: {
  triggerType: VisibleTriggerType;
  triggers: ReturnType<typeof useInputTriggersIdentification>;
  disabledInputTriggers?: ReturnType<typeof useDisabledInputTriggers>;
  isFacebookConnected: boolean;
  isInstagramConnected: boolean;
  isSMSConnected: boolean;
  isEmailConnected: boolean;
  isAllowedToImportContacts: boolean;
}) => {
  const {
    isInstagramConnected,
    isSMSConnected,
    isAllowedToImportContacts,
    isFacebookConnected,
    triggers,
    triggerType,
    disabledInputTriggers,
    isEmailConnected,
  } = params;
  const hasIgWelcomer = triggers?.some(
    trigger =>
      trigger?.type === 'instagram' &&
      trigger?.label.toLowerCase().includes('welcomer'),
  );

  const hasFbWelcomer = triggers?.some(
    trigger =>
      trigger?.type === 'facebook' &&
      trigger?.label.toLowerCase().includes('welcomer'),
  );

  const standardDefinitions = {
    instagram: {
      disabled: !triggers || !isInstagramConnected,
      disabledTooltip: !isInstagramConnected
        ? 'Connect your Instagram account to start generating more leads'
        : '',
    },
    facebook: {
      disabled: !triggers || !isFacebookConnected,
      disabledTooltip: !isFacebookConnected
        ? 'Connect your Facebook page to start generating more leads'
        : '',
    },
    sms: {
      disabled: !triggers || !isSMSConnected,
      disabledTooltip: !isSMSConnected
        ? 'Claim phone number to start re-engaging your leads with texts!'
        : '',
    },
  } as const;

  const definitions: Record<
    VisibleTriggerType,
    Pick<TriggerCardDefinition, 'disabled' | 'disabledTooltip'>
  > = {
    fb_comment_guard: standardDefinitions.facebook,
    instagram_comment_guard: standardDefinitions.instagram,
    instagram_live_comment_guard: standardDefinitions.instagram,
    instagram_story_mention: standardDefinitions.instagram,
    instagram_story_reaction: standardDefinitions.instagram,
    instagram_keywords: standardDefinitions.instagram,
    facebook_keywords: standardDefinitions.facebook,
    import_contacts: {
      disabled: !isAllowedToImportContacts,
      disabledTooltip: !isAllowedToImportContacts ? (
        <>Connect an Email Account or activate a phone number first</>
      ) : (
        ''
      ),
    },
    link: standardDefinitions.facebook,
    instagram_link: standardDefinitions.instagram,
    ctm_ads: standardDefinitions.facebook,

    instagram_welcomer: {
      disabled:
        !triggers ||
        hasIgWelcomer ||
        !isInstagramConnected ||
        !!disabledInputTriggers?.templates?.instagram_welcomer,
      disabledTooltip: !isInstagramConnected
        ? 'Connect your Instagram account to start generating more leads'
        : disabledInputTriggers?.templates?.instagram_welcomer
        ? disabledInputTriggers.templates.instagram_welcomer
        : hasIgWelcomer
        ? 'Only one Instagram DM Autoresponder can be active at a time'
        : '',
    },
    welcome_page: {
      disabled:
        !triggers ||
        hasFbWelcomer ||
        !isFacebookConnected ||
        !!disabledInputTriggers?.templates?.welcome_page,
      disabledTooltip: !isFacebookConnected
        ? 'Connect your Facebook page to start generating more leads'
        : disabledInputTriggers?.templates?.welcome_page
        ? disabledInputTriggers.templates.welcome_page
        : hasFbWelcomer
        ? 'Only one Facebook DM Autoresponder can be active at a time'
        : '',
    },
    customer_chat: {},
    web_visitor_email_detector: {
      disabled: !isEmailConnected,
      disabledTooltip: !isEmailConnected
        ? 'Connect your Email account to start generating more leads'
        : '',
    },
    audience: {
      disabled: !!triggers?.find(trigger => trigger?.type === 'audience'),
      disabledTooltip: triggers?.find(trigger => trigger?.type === 'audience')
        ? 'Only one Automation Input trigger can be activated at a time. Edit your current input trigger or remove it to choose a new one'
        : '',
    },
  };
  return definitions[triggerType];
};

export const slideoutBannersByTriggerType = (triggerType: TriggerType) => {
  switch (triggerType) {
    case 'facebook_keywords':
      return <FBKeywordSlideoutBanner textColor="normal" />;
    case 'instagram_keywords':
      return <IGKeywordSlideoutBanner textColor="normal" />;
    case 'instagram_live_comment_guard':
      return <IGLiveSlideoutBanner textColor="normal" />;
    case 'instagram_story_mention':
      return <IGStoryMentionSlideoutBanner textColor="normal" />;
    case 'instagram_story_reaction':
      return <IGStoryReactionSlideoutBanner textColor="normal" />;
    case 'customer_chat':
      return <WebsiteChatSlideoutBanner textColor="normal" />;
    case 'instagram_welcomer':
      return <IGWelcomerSlideoutBanner textColor="normal" />;
    case 'instagram_link':
      return <IGShareLinksSlideoutBanner textColor="normal" />;
    case 'instagram_comment_guard':
      return <IGCommentGuardSlideoutBanner textColor="normal" />;
    case 'link':
      return <FBShareLinksSlideoutBanner textColor="normal" />;
    case 'fb_comment_guard':
      return <FBCommentGuardSlideoutBanner textColor="normal" />;
    default:
      return;
  }
};
